import {
    ORIGINAL_CHALLENGE_PREFIX,
    ORIGINAL_DATA_PREFIX,
} from 'features/offline-reports/support-requests/components/support-requests-modals/modals/request-new-location/request-property/flow-config/support-request-results-modal/utils';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import {
    MappedFileData,
    ParsedDataRow,
    RequestResultsStatus,
    SupportRequestResultsModalTableRow,
    SupportRequestResultsResponse,
} from 'features/offline-reports/support-requests/components/support-requests-modals/modals/request-new-location/request-property/types';
import { resolverBridgeQueryKeys } from 'features/offline-reports/support-requests/components/support-requests-modals/queries/resolver-queries-keys';
import isNil from 'lodash/isNil';
import type { DataGridProps } from '@placer-ui/components';
import { buildCSV, createCsvData } from 'utils/create-csv/create-csv';
import startCase from 'lodash/startCase';
import { useAtomValue } from 'jotai';
import { resolverJobStore } from 'features/offline-reports/support-requests/store/property-store';
import { challengeKeys } from '../modals/request-new-location/request-property/flow-config/support-request-results-modal/components/ask-for-review/utils';

export const DEFAULT_EMPTY_VALUE = '--';
export const normalizeString = (column: string) => column.toLowerCase().replace(/[^a-z0-9]/g, '');

export const getAllUniqueDataKeys = (data?: ParsedDataRow[]): string[] => {
    if (!data?.length) return [];

    return Array.from(new Set(data.flatMap(Object.keys)));
};

export const setDataLoadingStatuses = (itemIds: string[], data?: MappedFileData) => {
    if (!data) return data;

    return {
        ...data,
        data: data.data.map((item) => {
            if (itemIds.includes(String(item.id))) {
                return {
                    ...item,
                    isLoading: true,
                };
            }
            return item;
        }),
    };
};

export const resetDataStatuses = (data?: MappedFileData) => {
    if (!data) return data;
    return {
        ...data,
        data: data.data.map((item) => ({
            ...item,
            isLoading: false,
            isEdited: false,
        })),
    };
};

export const mergeResultsWithMappedFileData = (
    jobResults: SupportRequestResultsResponse[],
    mappedFileData: MappedFileData | undefined,
) => {
    if (!mappedFileData?.data.length) return [];

    const allKeys = getAllUniqueDataKeys(mappedFileData.data);

    return mappedFileData?.data
        .map(({ id }) => {
            const jobResult = jobResults.find((item) => item.id === id);
            if (!jobResult) return null;

            const columnsToKeepWithoutPrefix = [
                'id',
                'isEdited',
                'isLoading',
                'actions',
                'zip_code',
                ...challengeKeys,
            ];

            const row = allKeys.reduce((acc, key) => {
                const originalValue = mappedFileData.data.find((item) => item.id === id)?.[key];

                if (
                    isNil(mappedFileData.matching[key]) &&
                    !columnsToKeepWithoutPrefix.includes(key) &&
                    !key.startsWith(ORIGINAL_DATA_PREFIX)
                ) {
                    return {
                        ...acc,
                        [`${ORIGINAL_DATA_PREFIX}${key}`]: originalValue,
                    };
                }
                return { ...acc, [key]: originalValue };
            }, {});

            return { ...jobResult, ...row };
        })
        .filter(Boolean) as SupportRequestResultsResponse[];
};
export const useSetJobResultsQueryData = () => {
    const queryClient = useQueryClient();
    const resolverJob = useAtomValue(resolverJobStore);

    return useCallback(
        (
            callback: (
                oldData: SupportRequestResultsModalTableRow[],
            ) => SupportRequestResultsModalTableRow[],
        ) => {
            queryClient.setQueryData<SupportRequestResultsModalTableRow[]>(
                resolverBridgeQueryKeys.jobResults(resolverJob?.job_id),
                (oldData) => {
                    if (!oldData) return oldData;

                    return callback(oldData);
                },
            );
        },
        [queryClient, resolverJob?.job_id],
    );
};

export const formatStatusFromEnum = ({
    status,
    isEdited,
}: {
    status: RequestResultsStatus;
    isEdited?: boolean;
}) => {
    return `${startCase(status)}${isEdited ? ' (Edited)' : ''}`;
};

export const transformTableDataToCsv = ({
    columns,
    data,
    includeChallengeItems,
}: {
    columns: DataGridProps<SupportRequestResultsModalTableRow>['columns'];
    data: SupportRequestResultsModalTableRow[];
    includeChallengeItems?: boolean;
}) => {
    const columnsToAvoid = [
        'actions',
        'lineId',
        'id',
        'simulate_field_error_code',
        'simulate_status',
    ];
    const relevantColumns = columns.filter(
        ({ headerName, field }) => !!headerName && !columnsToAvoid.includes(field!),
    );

    relevantColumns.unshift({ headerName: 'Placer ID', field: 'entity_id' });
    relevantColumns.push({ headerName: 'Error Reason', field: 'error_message' });
    relevantColumns.push({ headerName: 'Report Link', field: 'link_to_report' });

    Object.keys(data[0]).forEach((key) => {
        const name = key.replace(ORIGINAL_DATA_PREFIX, '');

        const isChallengeItem = includeChallengeItems
            ? key.startsWith(ORIGINAL_CHALLENGE_PREFIX)
            : false;

        if (
            (!isChallengeItem && !key.includes(ORIGINAL_DATA_PREFIX)) ||
            columnsToAvoid.includes(name)
        )
            return;

        relevantColumns.push({
            headerName: `${name}${isChallengeItem ? '' : ' (Source)'}`,
            field: key as keyof SupportRequestResultsModalTableRow,
        });
    });

    const headers: string[] = relevantColumns.map(({ headerName }) => headerName!);
    const fields = relevantColumns.map(({ field }) => field!);

    const keysFormat: { [key: string]: any } = {
        status: (value: RequestResultsStatus) => formatStatusFromEnum({ status: value }),
    };

    const rows: (string | number)[][] = data.map((row) => {
        return fields.map((key: string) => {
            const value = row[key as keyof SupportRequestResultsModalTableRow];
            return keysFormat[key] ? keysFormat[key](value) : value;
        });
    });

    const csv = buildCSV(headers, rows);
    const fileData: Blob = createCsvData(csv);

    return { headers, rows, fileData };
};
